<template>
  <div class="warehouse-form">
    <div class="card" style="max-width: 900px;">
      <form class="form-grid" @submit.prevent="provideEquipment">
        <div class="card-header">
          <div>
            <h2 class="title" style="font-size: 22px;">Fornecer equipamento</h2>
            <p class="subtitle">Registre a saida de ferramenta para um colaborador.</p>
          </div>
        </div>

        <div class="grid" style="grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 14px;">
          <div class="input-field">
            <label for="provide-item">Ferramenta / peca</label>
            <select id="provide-item" v-model="form.itemId">
              <option value="">Selecione ferramenta/peca</option>
              <option v-for="item in items" :key="item.id" :value="item.id">
                {{ item.name }} (disp: {{ item.availableQuantity }})
              </option>
            </select>
          </div>

          <div class="input-field">
            <label for="provide-quantity">Quantidade</label>
            <input id="provide-quantity" v-model.number="form.quantity" type="number" min="1" />
          </div>

          <div class="input-field">
            <label for="provide-condition">Estado na saida</label>
            <input id="provide-condition" v-model="form.conditionStatus" type="text" />
          </div>
        </div>

        <div class="input-field collaborator-field">
          <label>Colaborador</label>
          <button class="select-trigger" type="button" @click="toggleDropdown">
            {{ selectedCollaboratorLabel || 'Selecionar colaborador' }}
          </button>
          <div v-if="isDropdownOpen" class="dropdown-panel">
            <input
              v-model="collaboratorQuery"
              class="dropdown-search"
              type="text"
              placeholder="Buscar colaborador"
            />
            <div class="user-list">
              <button
                v-for="user in filteredCollaborators"
                :key="user.username"
                class="user-option"
                type="button"
                @click="selectCollaborator(user.username)"
              >
                {{ user.name }} ({{ user.username }})
              </button>
              <div v-if="filteredCollaborators.length === 0" class="hint">Nenhum colaborador encontrado.</div>
            </div>
          </div>
        </div>

        <p v-if="error" class="subtitle" style="color: #a54b2a;">{{ error }}</p>

        <div class="actions">
          <button class="btn btn-primary" type="submit" :disabled="saving">{{ saving ? 'Fornecendo...' : 'Fornecer equipamento' }}</button>
          <button class="btn btn-ghost" type="button" :disabled="saving" @click="goBack">Cancelar</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { apiFetch } from '@/store/api'
import { warehouseStore } from '@/store/warehouse'

export default defineComponent({
  name: 'WarehouseProvideEquipmentForm',
  setup() {
    const router = useRouter()
    const collaborators = ref<Array<{ username: string; name: string }>>([])
    const collaboratorQuery = ref('')
    const isDropdownOpen = ref(false)
    const error = ref('')
    const saving = ref(false)

    const form = ref({
      itemId: '',
      username: '',
      quantity: 1,
      conditionStatus: 'bom'
    })

    const items = computed(() => warehouseStore.all())

    const filteredCollaborators = computed(() => {
      const query = collaboratorQuery.value.trim().toLowerCase()
      if (!query) return collaborators.value
      return collaborators.value.filter((user) => {
        const name = user.name.toLowerCase()
        const username = user.username.toLowerCase()
        return name.includes(query) || username.includes(query)
      })
    })

    const selectedCollaboratorLabel = computed(() => {
      if (!form.value.username) return ''
      const selected = collaborators.value.find((item) => item.username === form.value.username)
      if (!selected) return form.value.username
      return `${selected.name} (${selected.username})`
    })

    const refreshCollaborators = async () => {
      const rows = await apiFetch<Array<{ username: string; name: string }>>('/accounts')
      collaborators.value = rows.map((item) => ({
        username: String(item.username || ''),
        name: String(item.name || item.username || '')
      }))
    }

    const refreshAll = async () => {
      await Promise.all([warehouseStore.refresh(), refreshCollaborators()])
    }

    const toggleDropdown = () => {
      isDropdownOpen.value = !isDropdownOpen.value
    }

    const selectCollaborator = (username: string) => {
      form.value.username = username
      isDropdownOpen.value = false
      collaboratorQuery.value = ''
    }

    const provideEquipment = async () => {
      error.value = ''
      if (!form.value.itemId || !form.value.username) {
        error.value = 'Selecione ferramenta e colaborador.'
        return
      }
      saving.value = true
      try {
        await apiFetch('/warehouse/provide', {
          method: 'POST',
          body: JSON.stringify({
            itemId: form.value.itemId,
            username: form.value.username,
            quantity: form.value.quantity,
            conditionStatus: form.value.conditionStatus
          })
        })
        await router.push('/almoxarifado?tab=estoque')
      } finally {
        saving.value = false
      }
    }

    const goBack = () => {
      router.push('/almoxarifado?tab=estoque')
    }

    onMounted(() => {
      refreshAll().catch(() => {
        error.value = 'Nao foi possivel carregar dados do almoxarifado.'
      })
    })

    return {
      form,
      items,
      collaboratorQuery,
      filteredCollaborators,
      selectedCollaboratorLabel,
      isDropdownOpen,
      error,
      saving,
      toggleDropdown,
      selectCollaborator,
      provideEquipment,
      goBack
    }
  }
})
</script>

<style scoped lang="scss">
.warehouse-form {
  width: 100%;
}

.collaborator-field {
  position: relative;
}

.select-trigger {
  width: 100%;
  border: 1px solid rgba(31, 30, 26, 0.2);
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 15px;
  text-align: left;
  background: #fff;
  cursor: pointer;
}

.dropdown-panel {
  margin-top: 8px;
  border: 1px solid rgba(31, 30, 26, 0.15);
  border-radius: 12px;
  padding: 10px;
  background: #fff;
}

.dropdown-search {
  width: 100%;
  border: 1px solid rgba(31, 30, 26, 0.2);
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 8px;
}

.user-list {
  max-height: 220px;
  overflow-y: auto;
  display: grid;
  gap: 6px;
}

.user-option {
  width: 100%;
  text-align: left;
  background: rgba(31, 30, 26, 0.03);
  border: 1px solid rgba(31, 30, 26, 0.08);
  border-radius: 10px;
  padding: 8px 10px;
  cursor: pointer;
}

.user-option:hover {
  border-color: rgba(31, 30, 26, 0.2);
}
</style>
